import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../../services/api";
import CustomTable from "../../components/Tables/CustomTable";
import { FiDownload } from "react-icons/fi";
import ToastManager from "../../components/Toast/ToastManager";
import { useToast } from "../../hooks/useToast";
import AplicarFiltrosButton from "../../components/Buttons/AplicarFiltrosButton";
import HeatmapMG from "../../components/HeatMap/HeatmapMG";
import {
  ReportContainer,
  PageTitle,
  FiltersContainer,
  FiltersTitle,
  FiltersRow,
  FilterGroup,
  FilterLabel,
  FilterSelect,
  FilterInput,
  ButtonsGroup,
  DownloadButton,
  HeatMapCard,
  HeatMapTitle,
  HeatMapSubtitle,
  HeatMapContent
} from "./IndexPage.styles";

const ReportIndexPage = () => {
  const location = useLocation();
  const isMapMode = location.pathname.includes("/relatorios/mapa");
  const [kitsPorMunicipio, setKitsPorMunicipio] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isClearingFilters, setIsClearingFilters] = useState(false);
  
  const { toasts, removeToast, showSuccess, showError } = useToast();
  
  const [tipoRelatorio, setTipoRelatorio] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [regiao, setRegiao] = useState("");
  const [quantidadeKits, setQuantidadeKits] = useState("");
  
  const [municipios, setMunicipios] = useState([]);
  const [regionais, setRegionais] = useState([]);
  const [anoMapa, setAnoMapa] = useState("consolidado");
  const [statusGestanteMapa, setStatusGestanteMapa] = useState("todas");

  useEffect(() => {
    if (isMapMode) return;

    const fetchMunicipios = async () => {
      try {
        const page = 1;
        const size = 853;
        const response = await api.get("/municipios/list", {
          params: { page, size },
        });
        setMunicipios(response.data);
        console.log("Municípios carregados:", response.data);
      } catch (error) {        
        console.error("Erro ao buscar municípios:", error);
      }
    };  
    
    const fetchRegionais = async () => {
      try {
        const response = await api.get("/regionais/list", {
          params: { page: 1, size: 28 },
        });
        setRegionais(response.data);        
      } catch (error) {
        console.error("Erro ao buscar regionais:", error);
      }
    };    

    fetchRegionais();
    fetchMunicipios();
  }, [isMapMode]);

  const fetchKitsPorMunicipio = async (page = 1, size = 10) => {
    try {
      const params = { page, size };
      if (tipoRelatorio) params.tipo_relatorio = tipoRelatorio;
      if (municipio) params.municipio = municipio;
      if (regiao) params.regiao = regiao;
      if (quantidadeKits) params.quantidade_kits = quantidadeKits;
      
      const kitsPorMunicipioResponse = await api.get(`/relatorios/kits-por-municipio`, {
        params: params
      });
      
      if (kitsPorMunicipioResponse.data.data && kitsPorMunicipioResponse.data.pagination) {
        const { data: reportData, pagination } = kitsPorMunicipioResponse.data;
        setKitsPorMunicipio(reportData);
        setCurrentPage(pagination.current_page);
        setHasNextPage(pagination.has_next);
      } else {
        setKitsPorMunicipio(kitsPorMunicipioResponse.data);
        setHasNextPage(false);
      }
            
    } catch (error) {
      console.error("Erro ao buscar kits por município: ", error);
      
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError("Erro ao carregar dados dos relatórios. Tente novamente.");
      }
    }
  };
  const columns = [
    { field: "municipio", headerName: "Município" },
    {
      field: "quantidade_kits",
      headerName: tipoRelatorio === "1" ? "Quantidade de Kits Entregues" : "Quantidade de Kits",
    },
  ];

  const handleDownloadExcel = async (cod_ibge) => {
    try {
      const params = { cod_ibge };
      if (tipoRelatorio) params.tipo_relatorio = tipoRelatorio;
      
      const response = await api.get(`/relatorios/gestantes-aptas`, {
        params: params,
        responseType: "blob",
      });

      if (response.data.size === 0) {
        showError("Nenhuma gestante apta encontrada para este município.");
        return;
      }

      const blob = new Blob([response.data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `gestantes_${cod_ibge}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("Relatório baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao baixar relatório:", error);
      
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError("Não foi possível baixar o relatório. Tente novamente.");
      }
    }
  };

  const handleApplyFilters = async () => {
    setIsApplyingFilters(true);
    try {
      await fetchKitsPorMunicipio(1);
      showSuccess("Filtros aplicados com sucesso!");
    } catch (error) {
      console.error("Erro ao aplicar filtros: ", error);
      
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError("Erro ao aplicar filtros. Tente novamente.");
      }
    } finally {
      setIsApplyingFilters(false);
    }
  };

  const handleClearFilters = async () => {
    setIsClearingFilters(true);
    try {
        setTipoRelatorio("");
        setMunicipio("");
        setRegiao("");
        setQuantidadeKits("");
        setKitsPorMunicipio([]);
        setCurrentPage(1);
        setHasNextPage(false);
        showSuccess("Filtros limpos com sucesso!");
    } catch (error) {
      console.error("Erro ao limpar filtros: ", error);
      showError("Erro ao limpar filtros. Tente novamente.");
    } finally {
      setIsClearingFilters(false);
    }
  };

  const handleDownloadSearchResults = async () => {
    setIsDownloadingReport(true);
    try {
      const params = {};
      if (tipoRelatorio) params.tipo_relatorio = tipoRelatorio;
      if (municipio) params.municipio = municipio;
      if (regiao) params.regiao = regiao;
      if (quantidadeKits) params.quantidade_kits = quantidadeKits;

      const response = await api.get(`/relatorios/baixar-relatorio-kits`, {
        params: params,
        responseType: "blob",
      });
      
      if (response.data.size === 0) {
        showError("Sem resultados para o filtro escolhido.");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const reportTypeLabel = tipoRelatorio === "1" ? "kits-entregues" : "kits-a-serem-entregues";
      link.setAttribute("download", `relatorio-${reportTypeLabel}.xlsx`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("Relatório baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao baixar relatório: ", error);
      
      if (error.response?.data?.detail) {
        showError(error.response.data.detail);
      } else {
        showError("Não foi possível baixar o relatório. Sem dados para o filtro aplicado.");
      }
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const renderActions = (row) => (
    <button
      onClick={() => handleDownloadExcel(row.cod_ibge)}
      style={{
        background: "none",
        border: "none",
        color: "#2f2f2f",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontWeight: 500,
      }}
      title="Baixar relatório de gestantes aptas"
    >
      <FiDownload size={16} />
      Baixar
    </button>
  );

  return (
    <ReportContainer>
      <PageTitle>{isMapMode ? "Relatórios" : "Relatório de Kits"}</PageTitle>
      
      {!isMapMode && (
        <>
          <FiltersContainer>
            <FiltersTitle>Filtros:</FiltersTitle>
            
            <FiltersRow>
              <FilterGroup>
                <FilterLabel>Tipo de Relatório*:</FilterLabel>
                <FilterSelect
                  value={tipoRelatorio}
                  onChange={(e) => setTipoRelatorio(e.target.value)}
                >
                  <option value="">Selecione uma opção</option>
                  <option value="1">Kits entregues</option>
                  <option value="2">Kits a serem entregues</option>
                </FilterSelect>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Município:</FilterLabel>
                <FilterSelect
                  value={municipio}
                  onChange={(e) => setMunicipio(e.target.value)}
                >
                  <option value="">Selecione um município</option>
                  {municipios.map((mun) => (
                    <option key={mun.cod_ibge} value={mun.cod_ibge}>
                      {mun.nome}
                    </option>
                  ))}
                </FilterSelect>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Região:</FilterLabel>
                <FilterSelect
                  value={regiao}
                  onChange={(e) => setRegiao(e.target.value)}
                >
                  <option value="">Selecione uma opção</option>
                  {regionais.map((reg) => (
                    <option key={reg.cod_urs} value={reg.cod_urs}>
                      {reg.nome}
                    </option>
                  ))}
                </FilterSelect>
              </FilterGroup>

              <FilterGroup>
                <FilterLabel>Quantidade de Kits:</FilterLabel>
                <FilterInput
                  type="number"
                  value={quantidadeKits}
                  onChange={(e) => setQuantidadeKits(e.target.value)}
                  placeholder="Informe uma quantidade"
                />
              </FilterGroup>

              <ButtonsGroup>
                <DownloadButton 
                  onClick={handleDownloadSearchResults}
                  disabled={!tipoRelatorio || isDownloadingReport || kitsPorMunicipio.length === 0}
                  style={{
                    opacity: (!tipoRelatorio || isDownloadingReport || kitsPorMunicipio.length === 0) ? 0.6 : 1,
                    cursor: (!tipoRelatorio || isDownloadingReport || kitsPorMunicipio.length === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isDownloadingReport ? (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                        style={{ marginRight: '8px' }}
                      >
                        <path d="M8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3Zm-7 5a7 7 0 1 1 14 0A7 7 0 0 1 1 8Z">
                          <animateTransform
                            attributeName="transform"
                            type="rotate"
                            from="0 8 8"
                            to="360 8 8"
                            dur="1s"
                            repeatCount="indefinite"
                          />
                        </path>
                      </svg>
                      Baixando...
                    </>
                  ) : (
                    <>
                      <FiDownload size={16} />
                      Baixar resultados da pesquisa
                    </>
                  )}
                </DownloadButton>
                
                <AplicarFiltrosButton 
                  onClick={handleApplyFilters}
                  isLoading={isApplyingFilters}
                  disabled={!tipoRelatorio || isApplyingFilters}
                  iconOnly={true}
                  tooltip="Aplicar Filtros"
                  icon="filter"
                />
                
                <AplicarFiltrosButton 
                  label="Limpar Filtros"
                  onClick={handleClearFilters}
                  isLoading={isClearingFilters}
                  disabled={isClearingFilters || (!tipoRelatorio && !municipio && !regiao && !quantidadeKits)}
                  iconOnly={true}
                  tooltip="Limpar Filtros"
                  icon="clear"
                />
              </ButtonsGroup>
            </FiltersRow>
          </FiltersContainer>

          <CustomTable
            columns={columns}
            data={kitsPorMunicipio}
            currentPage={currentPage}
            onPageChange={(page) => fetchKitsPorMunicipio(page)}
            hasNextPage={hasNextPage}
            renderActions={renderActions}
          />
        </>
      )}

      {isMapMode && (
        <FiltersContainer>
          <FiltersTitle>Filtros:</FiltersTitle>
          
          <FiltersRow>
            <FilterGroup>
              <FilterLabel>Ano:</FilterLabel>
              <FilterSelect
                value={anoMapa}
                onChange={(e) => setAnoMapa(e.target.value)}
              >
                <option value="2025">2025</option>
                <option value="2026">2026</option>
                <option value="consolidado">Consolidado</option>
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Status da gestante:</FilterLabel>
              <FilterSelect
                value={statusGestanteMapa}
                onChange={(e) => setStatusGestanteMapa(e.target.value)}
              >
                <option value="todas">Todas</option>
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
              </FilterSelect>
            </FilterGroup>
          </FiltersRow>
        </FiltersContainer>
      )}

      {isMapMode && (
        <HeatMapCard>
          <HeatMapTitle>Mapa de Calor</HeatMapTitle>
          <HeatMapSubtitle>kits entregues</HeatMapSubtitle>
          <HeatMapContent>
            <HeatmapMG ano={anoMapa} statusGestante={statusGestanteMapa} />
          </HeatMapContent>
        </HeatMapCard>
      )}

      <ToastManager toasts={toasts} removeToast={removeToast} />
    </ReportContainer>
  );
};

export default ReportIndexPage;
